import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './SearchBar.css';

function getBaseName(pokemonName) {
  return pokemonName
    .replace(/\s*\(Mega\)/, '')
    .replace(/\s*\(Shadow\)/, '')
    .trim();
}

function SearchBar({ allTierLists }) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [allPokemon, setAllPokemon] = useState([]);
  const navigate = useNavigate();
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Load the full pokemon search index
  useEffect(() => {
    fetch('/data/pokemon_search_index.json')
      .then(res => res.json())
      .then(data => setAllPokemon(data))
      .catch(() => {});
  }, []);

  // Build search index from full pokemon list, supplemented with tier list variant info
  const pokemonIndex = useMemo(() => {
    if (allPokemon.length === 0) return [];

    // Collect variant info from tier lists
    const tierVariants = new Map();
    if (allTierLists) {
      for (const type of Object.keys(allTierLists)) {
        const tierData = allTierLists[type];
        for (const category of ['mega', 'non_mega']) {
          if (!tierData[category]) continue;
          for (const entry of tierData[category]) {
            const baseName = getBaseName(entry.pokemon_name);
            if (!tierVariants.has(baseName)) {
              tierVariants.set(baseName, new Set());
            }
            tierVariants.get(baseName).add(entry.pokemon_name);
          }
        }
      }
    }

    return allPokemon.map(p => {
      const variants = tierVariants.get(p.name);
      return {
        baseName: p.name,
        icon_filename: p.icon_filename,
        variants: variants ? Array.from(variants) : [p.name]
      };
    });
  }, [allPokemon, allTierLists]);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return pokemonIndex
      .filter(p => p.baseName.toLowerCase().includes(q))
      .slice(0, 10);
  }, [query, pokemonIndex]);

  useEffect(() => {
    setHighlightIndex(-1);
  }, [results]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigateToProfile = (baseName) => {
    setQuery('');
    setIsOpen(false);
    navigate(`/pokemon/${encodeURIComponent(baseName)}`);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      navigateToProfile(results[highlightIndex].baseName);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div className="search-bar-wrapper" ref={wrapperRef}>
      <div className="search-bar-input-container">
        <span className="search-icon">🔍</span>
        <input
          ref={inputRef}
          type="text"
          className="search-bar-input"
          placeholder="Search Pokémon..."
          value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => { if (query.trim()) setIsOpen(true); }}
          onKeyDown={handleKeyDown}
        />
        {query && (
          <button className="search-clear" onClick={() => { setQuery(''); setIsOpen(false); }}>✕</button>
        )}
      </div>
      {isOpen && results.length > 0 && (
        <div className="search-dropdown">
          {results.map((pokemon, index) => (
            <div
              key={pokemon.baseName}
              className={`search-result ${index === highlightIndex ? 'highlighted' : ''}`}
              onMouseDown={() => navigateToProfile(pokemon.baseName)}
              onMouseEnter={() => setHighlightIndex(index)}
            >
              {pokemon.icon_filename && (
                <img
                  src={`/pokemon_icons/${pokemon.icon_filename}`}
                  alt={pokemon.baseName}
                  className="search-result-icon"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div className="search-result-info">
                <span className="search-result-name">{pokemon.baseName}</span>
                {pokemon.variants.length > 1 && (
                  <span className="search-result-variants">
                    {pokemon.variants.length} variants
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {isOpen && query.trim() && results.length === 0 && (
        <div className="search-dropdown">
          <div className="search-no-results">No Pokémon found</div>
        </div>
      )}
    </div>
  );
}

export default SearchBar;
